/* In-menu community profile editor — players never leave the main menu (and in-game
   never leave the game) to manage their citizen identity. Same APIs as /community. */
import { useEffect, useRef, useState } from "react";
import { Camera, ImageIcon, Save, X, Loader2, ShieldCheck, UserRound } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const ACCENTS = ["#2E6BFF", "#f97316", "#4ade80", "#f43f5e", "#c084fc", "#fbbf24", "#38bdf8"];
const PRIVACY_ROWS = [
  ["show_servers", "Show my servers"], ["show_stats", "Show my stats"],
  ["show_crew", "Show my crew"], ["allow_messages", "Allow citizen messages"],
];

export default function CommunityProfileEditor({ accent = "#2E6BFF", onClose, audio }) {
  const [profile, setProfile] = useState(null);
  const [bio, setBio] = useState("");
  const [flair, setFlair] = useState("");
  const [acc, setAcc] = useState(accent);
  const [privacy, setPrivacy] = useState({});
  const [optIn, setOptIn] = useState(null);
  const [busy, setBusy] = useState("");
  const avatarRef = useRef(null);
  const bannerRef = useRef(null);

  useEffect(() => {
    api.get("/nc/community/profile/me").then((r) => {
      const p = r.data.profile;
      setProfile(p);
      setBio(p.bio || "");
      setFlair(p.flair || "");
      setAcc(p.accent || accent);
      setPrivacy(p.privacy || {});
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get("/nc/marketing/opt-in").then((r) => setOptIn(r.data.agreed)).catch(() => setOptIn(false));
  }, [accent]);

  const toggleOptIn = async (v) => {
    setOptIn(v);
    try {
      const r = await api.post("/nc/marketing/opt-in", { agreed: v, source: "profile" });
      toast.success(r.data.message);
      audio?.tick();
    } catch (e) {
      setOptIn(!v);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const save = async () => {
    setBusy("save");
    try {
      await api.put("/nc/community/profile", { bio, flair, accent: acc, privacy });
      toast.success("Profile updated — the network sees it now");
      audio?.tick();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/nc/community/profile/image?kind=${kind}`, fd);
      toast.success(r.data.message);
      const p = await api.get("/nc/community/profile/me");
      setProfile(p.data.profile);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="menu-profile-editor" className="absolute inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-6"
      onClick={onClose}>
      <div className="w-full max-w-lg border border-white/15 bg-[#070a10] shadow-2xl flex flex-col max-h-full"
        style={{ animation: "ncFadeUp 0.35s cubic-bezier(0.16,1,0.3,1) both" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="h-[3px] w-full shrink-0" style={{ background: `linear-gradient(90deg, ${acc}, transparent)` }} />
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-white/10 shrink-0">
          <UserRound className="w-4 h-4" style={{ color: acc }} />
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-200">My Community Profile</p>
          <button data-testid="editor-close" onClick={onClose} className="ml-auto text-zinc-500 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
        </div>
        {!profile ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>
        ) : (
          <div className="p-5 flex flex-col gap-4 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
            {/* identity strip */}
            <div className="flex items-center gap-4">
              <div className="relative w-16 h-16 shrink-0 border border-white/15 bg-black/60 overflow-hidden">
                {profile.avatar ? <img src={profile.avatar} alt="" className="w-full h-full object-cover" />
                  : <span className="absolute inset-0 flex items-center justify-center font-mono font-bold text-xl" style={{ color: acc }}>{(profile.name || "?")[0]}</span>}
                <button data-testid="editor-avatar-upload" onClick={() => avatarRef.current?.click()} disabled={busy === "avatar"}
                  className="absolute inset-0 bg-black/60 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                  {busy === "avatar" ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : <Camera className="w-4 h-4 text-white" />}
                </button>
                <input ref={avatarRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload("avatar", e.target.files?.[0])} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-sans font-bold text-base text-zinc-50 truncate">{profile.name}</p>
                <p className="font-mono text-[9px] text-zinc-500 truncate">{profile.email}</p>
                <button data-testid="editor-banner-upload" onClick={() => bannerRef.current?.click()} disabled={busy === "banner"}
                  className="mt-1.5 font-mono text-[8px] uppercase tracking-[0.2em] border border-white/15 text-zinc-400 hover:text-white hover:border-white/40 px-2 py-1 flex items-center gap-1.5 transition-colors disabled:opacity-40">
                  {busy === "banner" ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
                  {profile.banner ? "Change banner" : "Upload banner"}
                </button>
                <input ref={bannerRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload("banner", e.target.files?.[0])} />
              </div>
            </div>
            <p className="font-mono text-[8px] text-zinc-600 leading-relaxed flex items-center gap-1.5 -mt-1">
              <ShieldCheck className="w-3 h-3 text-emerald-500 shrink-0" /> uploads are safety-scanned before going live — 3 rejections in 90 days = community ban
            </p>
            <div>
              <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Bio</p>
              <textarea data-testid="editor-bio" value={bio} onChange={(e) => setBio(e.target.value)} maxLength={300} rows={3}
                placeholder="Who are you in Bornwood?"
                className="w-full bg-black/50 border border-white/10 px-3 py-2 font-mono text-[11px] text-zinc-100 outline-none focus:border-white/40 resize-none transition-colors" />
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Flair</p>
                <input data-testid="editor-flair" value={flair} onChange={(e) => setFlair(e.target.value)} maxLength={40}
                  placeholder="e.g. Dockside legend"
                  className="w-full bg-black/50 border border-white/10 px-3 py-2 font-mono text-[11px] text-zinc-100 outline-none focus:border-white/40 transition-colors" />
              </div>
              <div>
                <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Accent</p>
                <div className="flex gap-1.5 pt-1.5">
                  {ACCENTS.map((c) => (
                    <button key={c} data-testid={`editor-accent-${c.slice(1)}`} onClick={() => { setAcc(c); audio?.tick(); }}
                      className={`w-6 h-6 border transition-transform ${acc === c ? "scale-110 border-white" : "border-transparent"}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
            </div>
            <div>
              <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">Privacy</p>
              <div className="grid grid-cols-2 gap-1.5">
                {PRIVACY_ROWS.map(([k, label]) => (
                  <label key={k} data-testid={`editor-privacy-${k}`} className="flex items-center gap-2 border border-white/10 bg-black/30 px-2.5 py-2 cursor-pointer">
                    <input type="checkbox" checked={!!privacy[k]} onChange={(e) => setPrivacy((p) => ({ ...p, [k]: e.target.checked }))}
                      className="w-3.5 h-3.5 accent-[#2E6BFF]" />
                    <span className="font-mono text-[9px] text-zinc-300">{label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">Communications</p>
              <label data-testid="editor-optin-updates" className="flex items-start gap-2 border border-white/10 bg-black/30 px-2.5 py-2 cursor-pointer">
                <input type="checkbox" checked={!!optIn} disabled={optIn === null} onChange={(e) => toggleOptIn(e.target.checked)}
                  className="mt-0.5 w-3.5 h-3.5 accent-[#2E6BFF] shrink-0" />
                <span className="font-mono text-[9px] text-zinc-300 leading-relaxed">
                  New Citizen updates &amp; announcements
                  <span className="block text-zinc-600 text-[8px] mt-0.5">be first to know when updates drop — revocable anytime, right here</span>
                </span>
              </label>
            </div>
            <button data-testid="editor-save" onClick={save} disabled={busy === "save"}
              className="w-full font-mono text-[10px] font-bold uppercase tracking-[0.25em] py-3 text-black flex items-center justify-center gap-2 transition-opacity hover:opacity-85 disabled:opacity-40"
              style={{ backgroundColor: acc }}>
              {busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save profile
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
